import { ShowcaseBlock } from "@shared-kernel/primary/shared/design-system/shared/showcase-block";

type ColorEntry = {
  name: string;
  className: string;
  variable?: string;
  value?: string;
};

const SEMANTIC_COLORS: ColorEntry[] = [
  { name: "background", className: "bg-background text-foreground border", variable: "--background" },
  { name: "foreground", className: "bg-foreground text-background", variable: "--foreground" },
  { name: "card", className: "bg-card text-card-foreground border", variable: "--card" },
  { name: "popover", className: "bg-popover text-popover-foreground border", variable: "--popover" },
  { name: "primary", className: "bg-primary text-primary-foreground", variable: "--primary" },
  { name: "secondary", className: "bg-secondary text-secondary-foreground", variable: "--secondary" },
  { name: "muted", className: "bg-muted text-muted-foreground", variable: "--muted" },
  { name: "accent", className: "bg-accent text-accent-foreground", variable: "--accent" },
  { name: "destructive", className: "bg-destructive text-destructive-foreground", variable: "--destructive" },
  { name: "border", className: "bg-border text-foreground", variable: "--border" },
  { name: "input", className: "bg-input text-foreground", variable: "--input" },
  { name: "ring", className: "bg-ring text-foreground", variable: "--ring" },
];

const BRAND_COLORS: ColorEntry[] = [
  { name: "theme", className: "bg-theme text-white", value: "#831b1b" },
  { name: "success", className: "bg-success text-white", value: "#33c300" },
];

const CHART_COLORS: ColorEntry[] = [
  { name: "chart-1", className: "bg-chart-1 text-foreground", variable: "--chart-1", value: "hsl(18 58% 44%)" },
  { name: "chart-2", className: "bg-chart-2 text-foreground", variable: "--chart-2", value: "hsl(251 86% 75%)" },
  { name: "chart-3", className: "bg-chart-3 text-foreground", variable: "--chart-3", value: "hsl(45 30% 82%)" },
  { name: "chart-4", className: "bg-chart-4 text-foreground", variable: "--chart-4", value: "hsl(257 48% 88%)" },
  { name: "chart-5", className: "bg-chart-5 text-foreground", variable: "--chart-5", value: "hsl(30 50% 50%)" },
];

const SIDEBAR_COLORS: ColorEntry[] = [
  { name: "sidebar", className: "bg-sidebar text-sidebar-foreground border", variable: "--sidebar-background" },
  {
    name: "sidebar-primary",
    className: "bg-sidebar-primary text-sidebar-primary-foreground",
    variable: "--sidebar-primary",
  },
  {
    name: "sidebar-accent",
    className: "bg-sidebar-accent text-sidebar-accent-foreground",
    variable: "--sidebar-accent",
  },
];

const SPACING_SCALE = [0, 0.5, 1, 2, 3, 4, 6, 8, 12, 16, 24];

const RADII = [
  { name: "sm", className: "rounded-sm" },
  { name: "md", className: "rounded-md" },
  { name: "lg", className: "rounded-lg" },
  { name: "full", className: "rounded-full" },
];

const SHADOWS = [
  { name: "2xs", className: "shadow-2xs" },
  { name: "xs", className: "shadow-xs" },
  { name: "sm", className: "shadow-sm" },
  { name: "DEFAULT", className: "shadow" },
  { name: "md", className: "shadow-md" },
  { name: "lg", className: "shadow-lg" },
  { name: "xl", className: "shadow-xl" },
  { name: "2xl", className: "shadow-2xl" },
];

const ColorSwatch = ({ name, className, variable, value }: ColorEntry) => (
  <div className="space-y-1">
    <div className={`flex h-16 w-full items-center justify-center rounded-md ${className}`}>
      <span className="text-xs font-medium">Aa</span>
    </div>
    <div className="space-y-0.5">
      <p className="text-xs font-medium">{name}</p>
      {variable && <p className="font-mono text-[10px] text-muted-foreground">{variable}</p>}
      {value && <p className="font-mono text-[10px] text-muted-foreground">{value}</p>}
    </div>
  </div>
);

export const FoundationsSection = () => {
  return (
    <div className="space-y-8">
      <ShowcaseBlock
        id="couleurs-semantiques"
        title="Couleurs sémantiques"
        description="Tokens HSL exposés via les variables CSS dans input.css."
      >
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {SEMANTIC_COLORS.map(color => (
            <ColorSwatch key={color.name} {...color} />
          ))}
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock id="couleurs-marque" title="Couleurs marque" description="Définies en dur dans tailwind.config.js.">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6">
          {BRAND_COLORS.map(color => (
            <ColorSwatch key={color.name} {...color} />
          ))}
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock id="couleurs-graphiques" title="Couleurs graphiques" description="Pour les visualisations Recharts.">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {CHART_COLORS.map(color => (
            <ColorSwatch key={color.name} {...color} />
          ))}
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock id="couleurs-sidebar" title="Couleurs sidebar">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {SIDEBAR_COLORS.map(color => (
            <ColorSwatch key={color.name} {...color} />
          ))}
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock id="typographie" title="Typographie" description="Police par défaut, échelle de tailles Tailwind.">
        <div className="space-y-3">
          <p className="text-4xl font-bold">text-4xl / font-bold — Titre h1</p>
          <p className="text-3xl font-bold">text-3xl / font-bold — Titre h2</p>
          <p className="text-2xl font-semibold">text-2xl / font-semibold — Titre h3</p>
          <p className="text-xl font-semibold">text-xl / font-semibold — Titre h4</p>
          <p className="text-lg font-medium">text-lg / font-medium — Sous-titre</p>
          <p className="text-base">text-base — Corps de texte par défaut</p>
          <p className="text-sm">text-sm — Petit texte / label</p>
          <p className="text-xs text-muted-foreground">text-xs / muted — Aide / méta</p>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock id="espacement" title="Espacement" description="Échelle Tailwind (1 = 0.25rem = 4px).">
        <div className="space-y-2">
          {SPACING_SCALE.map(scale => (
            <div key={scale} className="flex items-center gap-3">
              <span className="w-12 text-xs text-muted-foreground">p-{scale}</span>
              <div className="bg-primary/20" style={{ width: `${scale * 0.25}rem`, height: "1rem" }} />
              <span className="text-xs text-muted-foreground">{scale * 0.25}rem</span>
            </div>
          ))}
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock id="border-radius" title="Border radius" description="Variables --radius dérivées.">
        <div className="flex flex-wrap gap-6">
          {RADII.map(r => (
            <div key={r.name} className="space-y-1.5 text-center">
              <div className={`size-20 border bg-card ${r.className}`} />
              <p className="text-xs text-muted-foreground">{r.name}</p>
            </div>
          ))}
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock id="ombres" title="Ombres" description="Tokens --shadow-*.">
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
          {SHADOWS.map(s => (
            <div key={s.name} className="space-y-1.5 text-center">
              <div className={`h-20 w-full rounded-md bg-card ${s.className}`} />
              <p className="text-xs text-muted-foreground">{s.name}</p>
            </div>
          ))}
        </div>
      </ShowcaseBlock>
    </div>
  );
};
